import { ReactNode } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import parse, { type DOMNode, Element, domToReact } from 'html-react-parser';
import { Anchorme } from 'react-anchorme';
import Link from '@alltrails/denali/components/Link';
import type Alert from '@alltrails/shared/types/alert';

/**
 * @param alerts an array of alerts
 * @returns an array of alerts with a description field with clickable links
 */
export const getDisplayableAlerts = (alerts: Alert[], isParkAlert?: boolean): Array<Alert & { description: ReactNode }> => {
  return alerts.map(alert => ({
    ...alert,
    description: isParkAlert ? (
      <div>
        {parse(DOMPurify.sanitize(alert.content), {
          replace: (domNode: DOMNode) => {
            if (domNode instanceof Element && domNode.attribs && domNode.name === 'a' && domNode.attribs?.href) {
              return (
                <Link href={domNode.attribs.href} target="_blank" rel="noopener" variant="secondary">
                  {domToReact(domNode.children as DOMNode[])}
                </Link>
              );
            }
            if (domNode.type === 'text' && 'data' in domNode && typeof domNode.data === 'string') {
              const urlRegex = /(https?:\/\/[^\s]+)/g;
              const parts = domNode.data.split(urlRegex);
              return (
                <>
                  {parts.map((part, index) => {
                    const isLink = urlRegex.test(part);
                    return isLink ? (
                      <Link key={index} href={part} target="_blank" rel="noopener" variant="secondary">
                        {part}
                      </Link>
                    ) : (
                      <span key={index}>{part}</span>
                    );
                  })}
                </>
              );
            }
            return undefined;
          }
        })}
      </div>
    ) : (
      <Anchorme
        linkComponent={props => (
          <Link href={props.href} target="_blank" rel="noopener">
            {props.children}
          </Link>
        )}
      >
        {alert.content}
      </Anchorme>
    ),
    id: alert.id as number
  }));
};
